import React, { useEffect, useRef, useState } from "react";
import { Card, Row, Col, Space, Typography, DatePicker, Flex, Select, Skeleton } from "antd";
import { ClockCircleOutlined, LaptopOutlined, UserOutlined, FileOutlined } from "@ant-design/icons";
import { useMutation } from "@apollo/client";
import { GET_ATTENDANCE_STATS } from "../../../../actions/graphql/mutations/attendance/attendance";
import moment from "moment";
import { useSelector } from "react-redux";
const { Title, Text } = Typography;

type AttendanceStats = {
    totalWorkingDays: number
    totalPresentDays: number
    totalAbsentDays: number
    totalHours: number
    averageDailyHours: number
};

type GetAttendanceStats = {
    getUserAttendanceStats: AttendanceStats
};

interface props {
    onDateChange?: (e) => void
}

const DashboardStats: React.FC<props> = ({ onDateChange }) => {
    const { user } = useSelector((state: any) => state.app);
    const [onChangeDate, setOnChangeDate] = useState(false)
    const [year, setSelectedYear] = useState(moment().format('YYYY'))
    const [month, setSelectedMonth] = useState(new Date().getMonth() + 1)
    const [yearMonthMap, setYearMonthMap] = useState<any>({});

    const [getUserAttendanceStats, { data, loading, error }] = useMutation<GetAttendanceStats>(GET_ATTENDANCE_STATS);

    useEffect(() => {
        handleFetch();
    }, [])

    useEffect(() => {
        if (onDateChange && year && month && onChangeDate) {
            onDateChange({ month, year });
            handleFetch();
            setOnChangeDate(false)
        }
    }, [year, month, onChangeDate]);

    useEffect(() => {
        if (user?.dateOfJoining) {
            const resp = getYearMonthMap(user?.dateOfJoining);
            setYearMonthMap(resp)
        }
    }, [user])

    const handleFetch = () => {
        getUserAttendanceStats({
            variables: { month: `${month}-${year}` }, // example value
        });
    };

    const stats = [
        {
            title: "Average Daily Hours",
            value: loading ? <Skeleton.Input active size="small" style={{ width: 80 }} /> : `${data?.getUserAttendanceStats?.averageDailyHours ?? '--'} Hr`,
            color: "#EAE5FE",
            icon: <ClockCircleOutlined style={{ color: "#8B5CF6" }} />,
        },
        {
            title: "Total Working Days",
            value: loading ? <Skeleton.Input active size="small" style={{ width: 80 }} /> : `${data?.getUserAttendanceStats?.totalWorkingDays ?? '--'} Days`,
            color: "#D4F4F4",
            icon: <LaptopOutlined style={{ color: "#10B981" }} />,
        },
        {
            title: "Total Present Days",
            value: loading ? <Skeleton.Input active size="small" style={{ width: 80 }} /> : `${data?.getUserAttendanceStats?.totalPresentDays ?? '--'} Days`,
            color: "#E0F2FF",
            icon: <UserOutlined style={{ color: "#3B82F6" }} />,
        },
        {
            title: "Total Absent Days",
            value: loading ? <Skeleton.Input active size="small" style={{ width: 80 }} /> : `${data?.getUserAttendanceStats?.totalAbsentDays ?? '--'} Days`,
            color: "#FFE4E6",
            icon: <FileOutlined style={{ color: "#EF4444" }} />,
        },
    ];

    function getYearMonthMap(fromDateStr) {
        const fromDate = new Date(fromDateStr);
        const toDate = new Date(); // current date

        const yearMonthMap = {};
        const startYear = fromDate.getFullYear();
        const endYear = toDate.getFullYear();

        for (let year = startYear; year <= endYear; year++) {
            let startMonth = 0;
            let endMonth = 11;

            if (year === startYear) startMonth = fromDate.getMonth(); // e.g., June = 5
            if (year === endYear) endMonth = toDate.getMonth(); // e.g., June = 5

            const months = [];
            for (let m = startMonth; m <= endMonth; m++) {
                months.push({
                    value: m + 1, // 1-indexed (Jan=1)
                    label: new Date(2000, m, 1).toLocaleString('default', { month: 'long' })
                });
            }

            yearMonthMap[year] = months;
        }

        return yearMonthMap;
    }


    return (
        <Flex justify="space-between">
            <Space direction="horizontal" size={"large"}>
                {stats?.map((stat, index) => (
                    <Card
                        bordered={false}
                        style={{
                            backgroundColor: stat.color,
                            borderRadius: 8,
                            height: "100%",
                        }}
                    >
                        <Space direction="horizontal" size={"middle"}>
                            <div style={{ fontSize: "24px", marginBottom: "8px" }}>{stat.icon}</div>
                            <div className="contact_info">
                                <Title level={5} className="noLine">{stat.title}</Title>
                                <Text>{stat.value}</Text>
                            </div>
                        </Space>
                    </Card>
                ))
                }
            </Space>
            <Space direction="horizontal">
                <Space direction="vertical" className="contact_info">
                    <Title level={5} className="noLine">Year</Title>
                    <Select placeholder={'Year'} className="w-30" defaultValue={year} onChange={e => {
                        setSelectedYear(e)
                        setOnChangeDate(true)
                    }} >
                        {Object.keys(yearMonthMap).map(ele => <Select.Option value={ele}>{ele}</Select.Option>)}
                    </Select>
                </Space>
                <Space direction="vertical" className="contact_info">
                    <Title level={5} className="noLine">Month</Title>
                    <Select placeholder={'Month'} className="w-40" defaultValue={month} onChange={(e) => {
                        setSelectedMonth(e)
                        setOnChangeDate(true)
                    }}>
                        {year && yearMonthMap[year]?.map(ele => <Select.Option value={ele?.value}>{ele?.label}</Select.Option>)}
                    </Select>
                </Space>
            </Space>
        </Flex>
    );
};

export default DashboardStats;
